// ─────────────────────────────────────────────────────────────────────────────
// /legal — THE COUNSEL REVIEW TRACKER.
//
// ⚠️ IT TRACKS DOCUMENTS, IT DOES NOT HOLD THEM. There is no file input on this
// page, no drop zone, and no endpoint behind either — the artifacts live in a
// data room with per-firm permissions, because eight firms each see only their
// own category and this page's auth cannot express that. "Where it lives" is a
// reference; see backend/test/legalTracker.test.js, which fails if a way to
// attach a file appears in the schema or the router.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const BRAND = {
  blue: '#3a6ad9', teal: '#14B8A6', orange: '#FF7A1A', gold: '#C9A84C',
  slate: '#64748B', error: '#ff082f', line: '#E7EAF0',
};

const SUB_TABS = ['Checklist', 'Artifacts', 'Findings', 'Notes'];

const CHECKLIST_STATUS = [
  ['not_started', 'Not started', BRAND.slate],
  ['in_review', 'In review', BRAND.blue],
  ['finding_raised', 'Finding raised', BRAND.orange],
  ['remediated', 'Remediated', BRAND.gold],
  ['verified', 'Verified', BRAND.teal],
];
const ARTIFACT_STATUS = [
  ['not_assembled', 'Not assembled', BRAND.slate],
  ['assembled', 'Assembled', BRAND.blue],
  ['delivered', 'Delivered to counsel', BRAND.teal],
];
const SEVERITY = [['must_fix', 'Must fix', BRAND.error], ['should_fix', 'Should fix', BRAND.orange], ['advisory', 'Advisory', BRAND.slate]];
const FINDING_STATUS = [['open', 'Open', BRAND.orange], ['remediated', 'Remediated', BRAND.gold],
  ['verified', 'Verified by counsel', BRAND.teal], ['accepted_risk', 'Accepted risk', BRAND.slate]];

const card = { background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #f3f4f6', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' };
const input = { padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff', width: '100%' };
// Explicit nulls rather than a sparse array — `[, , x]` reads as a typo, and
// eslint is right that it usually is one.
const toneOf = (list, v) => (list.find(([k]) => k === v) || [null, null, BRAND.slate])[2];
const labelOf = (list, v) => (list.find(([k]) => k === v) || [null, v])[1];

function Pill({ list, value }) {
  const c = toneOf(list, value);
  return (
    <span style={{ fontSize: 10, fontWeight: 800, color: c, background: `${c}1a`,
      padding: '3px 8px', borderRadius: 9999, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
      {labelOf(list, value)}
    </span>
  );
}

export default function AdminLegal() {
  const token = localStorage.getItem('doorly_admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [data, setData] = useState(null);
  const [cat, setCat] = useState('A');
  const [sub, setSub] = useState('Checklist');
  const [saving, setSaving] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  // 🔑 WHICH CHECKLIST ROW IS EXPANDED. One at a time: a counsel item runs to
  // about 1,200 characters and two open at once buries the rest of the plan.
  const [openItem, setOpenItem] = useState(null);
  const [newFinding, setNewFinding] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/legal`, { headers });
      setData(r.data);
    } catch (_) { setData({ categories: [], items: [], findings: [], notes: {} }); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setNoteDraft(data?.notes?.[cat]?.body || ''); }, [cat, data]);

  const patchItem = async (id, patch) => {
    setSaving(id);
    setData((d) => ({ ...d, items: d.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
    try { await axios.patch(`${API}/api/legal/items/${id}`, patch, { headers }); } catch (_) { load(); }
    setSaving(null);
  };
  const patchFinding = async (id, patch) => {
    setSaving(`f${id}`);
    try {
      await axios.patch(`${API}/api/legal/findings/${id}`, patch, { headers });
      await load();
    } catch (e) { alert(e.response?.data?.error || 'Could not save.'); await load(); }
    setSaving(null);
  };
  const saveNote = async () => {
    setSaving('note');
    try { await axios.put(`${API}/api/legal/notes/${cat}`, { body: noteDraft }, { headers }); await load(); } catch (_) {}
    setSaving(null);
  };
  const addFinding = async () => {
    try {
      await axios.post(`${API}/api/legal/findings`, { ...newFinding, category: cat }, { headers });
      setNewFinding(null); await load();
    } catch (e) { alert(e.response?.data?.error || 'Could not record it.'); }
  };

  const cats = data?.categories || [];
  const items = (kind) => (data?.items || []).filter((i) => i.category === cat && i.kind === kind);
  const findings = (data?.findings || []).filter((f) => f.category === cat);
  const openFindings = (c) => (data?.findings || []).filter((f) => f.category === c && f.status === 'open').length;

  const StatusSelect = ({ row, list }) => (
    <select value={row.status} disabled={saving === row.id}
      onChange={(e) => patchItem(row.id, { status: e.target.value })}
      style={{ ...input, width: 'auto', fontSize: 12, fontWeight: 700, color: toneOf(list, row.status) }}>
      {list.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
    </select>
  );

  return (
    <AdminLayout>
      <div style={{ padding: '28px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 4px' }}>Counsel review</h1>
        {/* ⚠️ SAID ON THE PAGE, NOT ONLY IN THE CODE. The person who wants to
            attach a file here should meet the reason before they meet the
            absence of a button. */}
        <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.6, maxWidth: 760 }}>
          A tracker, not a document store. Nothing is uploaded here and no borrower data is held —
          the documents live in the data room, where each firm sees only its own category. This page
          records what is being reviewed, who owns it, where it lives, and what came back.
        </p>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {cats.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)}
              style={{ padding: '7px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 700,
                border: `1.5px solid ${cat === c.key ? BRAND.blue : '#e5e7eb'}`,
                background: cat === c.key ? '#edf1fb' : '#fff', color: cat === c.key ? BRAND.blue : '#6b7280' }}>
              {c.key} · {c.label}
              {openFindings(c.key) > 0 && (
                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: BRAND.orange }}>
                  {openFindings(c.key)}
                </span>
              )}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 2, marginBottom: 14, borderBottom: `1px solid ${BRAND.line}` }}>
          {SUB_TABS.map((t) => (
            <button key={t} onClick={() => setSub(t)}
              style={{ padding: '9px 14px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', marginBottom: -1,
                borderTopLeftRadius: 8, borderTopRightRadius: 8,
                background: sub === t ? '#fff' : 'transparent', color: sub === t ? BRAND.blue : '#6b7280',
                border: sub === t ? `1px solid ${BRAND.line}` : '1px solid transparent',
                borderBottomColor: sub === t ? '#fff' : 'transparent' }}>
              {t}
            </button>
          ))}
        </div>

        {!data && <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div>}

        {data && sub === 'Checklist' && (
          <div style={card}>
            {/* ── 🚨 A CHECKLIST ITEM'S notes AND location WERE STORED AND NEVER
                   SHOWN ────────────────────────────────────────────────────────
                Jesse: "For each item: title, the question for counsel, the facts
                with dates and ids, and the relevant files or docs attached or
                linked."

                🔑 THE ROW ALREADY HAD THE FIELDS. legal_items carries notes and
                location, PATCH /legal/items/:id accepts both for either kind,
                and the ARTIFACT render shows location — but the checklist render
                drew only name, owner and status. Seeding a question and its
                facts into notes would have written them where nobody could read
                them, which is indistinguishable from not recording them.

                ⚠️ COLLAPSED BY DEFAULT. Eight counsel items carry roughly 1,200
                characters each; expanded inline they would bury the other 161
                rows of the plan. The marker is the affordance — a row with
                nothing to say shows no toggle at all. */}
            {items('checklist').map((i) => (
              <div key={i.id} style={{ padding: '9px 0', borderTop: `1px solid #f3f4f6` }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ flex: '1 1 340px', fontSize: 13.5, color: '#111827' }}>
                    {(i.notes || i.location) && (
                      <button
                        type="button"
                        onClick={() => setOpenItem((o) => (o === i.id ? null : i.id))}
                        title="Show the question, the facts and the linked files"
                        style={{ marginRight: 8, padding: '0 6px', fontSize: 11, fontWeight: 700,
                          color: BRAND.blue, background: '#eef2ff', border: `1px solid ${BRAND.blue}33`,
                          borderRadius: 4, cursor: 'pointer' }}
                      >{openItem === i.id ? '▾' : '▸'} detail</button>
                    )}
                    {i.name}
                  </span>
                  <input placeholder="Owner" defaultValue={i.owner || ''}
                    onBlur={(e) => e.target.value !== (i.owner || '') && patchItem(i.id, { owner: e.target.value })}
                    style={{ ...input, width: 130 }} />
                  <StatusSelect row={i} list={CHECKLIST_STATUS} />
                </div>
                {openItem === i.id && (
                  <div style={{ margin: '8px 0 4px', padding: '10px 12px', background: '#f9fafb',
                    border: `1px solid ${BRAND.line}`, borderRadius: 8 }}>
                    {/* ⚠️ EDITABLE IN PLACE, because counsel's answer belongs on
                        the item and the route already accepts it. The seed sets
                        notes on INSERT only, so an edit here is never overwritten
                        by a redeploy. */}
                    <textarea
                      defaultValue={i.notes || ''}
                      placeholder="The question for counsel, the facts, and counsel's answer."
                      onBlur={(e) => e.target.value !== (i.notes || '')
                        && patchItem(i.id, { notes: e.target.value })}
                      style={{ ...input, width: '100%', minHeight: 170, fontSize: 12.5,
                        lineHeight: 1.5, whiteSpace: 'pre-wrap', fontFamily: 'inherit' }} />
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af',
                        textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>
                        Files and docs
                      </div>
                      <input
                        defaultValue={i.location || ''}
                        placeholder="Files, docs or a data-room link"
                        onBlur={(e) => e.target.value !== (i.location || '')
                          && patchItem(i.id, { location: e.target.value })}
                        style={{ ...input, width: '100%', fontSize: 12.5 }} />
                      {/^https?:\/\//.test(i.location || '') && (
                        <a href={i.location} target="_blank" rel="noreferrer"
                          style={{ fontSize: 11.5, color: BRAND.blue }}>open link</a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
            {items('checklist').length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>Nothing in this category.</div>}
          </div>
        )}

        {data && sub === 'Artifacts' && (
          <div style={card}>
            {/* ⚠️ "WHERE IT LIVES" IS A REFERENCE, AND THE PLACEHOLDER SAYS SO.
                A data-room link or a plain description of where to find it —
                there is nowhere on this page a file could go. */}
            {items('artifact').map((i) => (
              <div key={i.id} style={{ padding: '10px 0', borderTop: '1px solid #f3f4f6' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ flex: '1 1 320px', fontSize: 13.5, color: '#111827' }}>{i.name}</span>
                  <input placeholder="Owner" defaultValue={i.owner || ''}
                    onBlur={(e) => e.target.value !== (i.owner || '') && patchItem(i.id, { owner: e.target.value })}
                    style={{ ...input, width: 130 }} />
                  <StatusSelect row={i} list={ARTIFACT_STATUS} />
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                  <input placeholder="Where it lives — data-room link or where to find it"
                    defaultValue={i.location || ''}
                    onBlur={(e) => e.target.value !== (i.location || '') && patchItem(i.id, { location: e.target.value })}
                    style={{ ...input, flex: 1 }} />
                  {/^https?:\/\//.test(i.location || '') && (
                    <a href={i.location} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, fontWeight: 700, color: BRAND.blue, whiteSpace: 'nowrap' }}>Open ↗</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {data && sub === 'Findings' && (
          <div style={card}>
            {/* ⚠️ THIS IS THE REMEDIATION LOG THE MEMORANDUM RESTS ON. "We fixed
                it and proved it" is a record here or a claim everywhere else,
                which is why verified needs a name and a date. */}
            <button onClick={() => setNewFinding({ severity: 'should_fix', finding: '' })}
              style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: BRAND.blue,
                color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer', marginBottom: 12 }}>
              + Record a finding
            </button>
            {newFinding && (
              <div style={{ border: `1px solid ${BRAND.line}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
                <textarea rows={3} placeholder="Counsel's item, in their words"
                  value={newFinding.finding} onChange={(e) => setNewFinding((p) => ({ ...p, finding: e.target.value }))}
                  style={{ ...input, resize: 'vertical', marginBottom: 8 }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <select value={newFinding.severity} onChange={(e) => setNewFinding((p) => ({ ...p, severity: e.target.value }))}
                    style={{ ...input, width: 'auto' }}>
                    {SEVERITY.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                  </select>
                  <input placeholder="Raised by" value={newFinding.raised_by || ''}
                    onChange={(e) => setNewFinding((p) => ({ ...p, raised_by: e.target.value }))} style={{ ...input, width: 160 }} />
                  <input type="date" value={newFinding.raised_at || ''}
                    onChange={(e) => setNewFinding((p) => ({ ...p, raised_at: e.target.value }))} style={{ ...input, width: 160 }} />
                  <button onClick={addFinding} style={{ padding: '7px 14px', borderRadius: 8, border: 'none',
                    background: BRAND.blue, color: '#fff', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Save</button>
                  <button onClick={() => setNewFinding(null)} style={{ padding: '7px 14px', borderRadius: 8,
                    border: '1px solid #e5e7eb', background: '#fff', color: '#6b7280', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
            {findings.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No findings recorded in this category.</div>}
            {findings.map((f) => (
              <div key={f.id} style={{ borderTop: '1px solid #f3f4f6', padding: '12px 0' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', marginBottom: 6 }}>
                  <Pill list={SEVERITY} value={f.severity} />
                  <Pill list={FINDING_STATUS} value={f.status} />
                  <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                    {f.raised_by ? `${f.raised_by}` : 'Counsel'}{f.raised_at ? ` · ${String(f.raised_at).slice(0, 10)}` : ''}
                  </span>
                </div>
                <div style={{ fontSize: 13.5, color: '#111827', whiteSpace: 'pre-wrap', marginBottom: 8 }}>{f.finding}</div>
                <textarea rows={2} placeholder="What we did about it"
                  defaultValue={f.remediation || ''}
                  onBlur={(e) => e.target.value !== (f.remediation || '') && patchFinding(f.id, { remediation: e.target.value })}
                  style={{ ...input, resize: 'vertical', marginBottom: 6 }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <input placeholder="Evidence — commit, document, or where to look"
                    defaultValue={f.evidence_ref || ''}
                    onBlur={(e) => e.target.value !== (f.evidence_ref || '') && patchFinding(f.id, { evidence_ref: e.target.value })}
                    style={{ ...input, flex: '1 1 240px' }} />
                  <input placeholder="Verified by (at the firm)" defaultValue={f.verified_by || ''}
                    onBlur={(e) => e.target.value !== (f.verified_by || '') && patchFinding(f.id, { verified_by: e.target.value })}
                    style={{ ...input, width: 190 }} />
                  <input type="date" defaultValue={f.verified_at ? String(f.verified_at).slice(0, 10) : ''}
                    onBlur={(e) => patchFinding(f.id, { verified_at: e.target.value || null })}
                    style={{ ...input, width: 150 }} />
                  <select value={f.status} onChange={(e) => patchFinding(f.id, { status: e.target.value })}
                    style={{ ...input, width: 'auto', fontWeight: 700, color: toneOf(FINDING_STATUS, f.status) }}>
                    {FINDING_STATUS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        )}

        {data && sub === 'Notes' && (
          <div style={card}>
            <textarea rows={16} value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="Working notes for this category. Calls, positions, open questions."
              style={{ ...input, resize: 'vertical', lineHeight: 1.6 }} />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
              <button onClick={saveNote} disabled={saving === 'note'}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: BRAND.blue,
                  color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                {saving === 'note' ? 'Saving…' : 'Save note'}
              </button>
              {data.notes?.[cat]?.updated_at && (
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                  Last saved {new Date(data.notes[cat].updated_at).toLocaleString()}
                  {data.notes[cat].updated_by ? ` by ${data.notes[cat].updated_by}` : ''}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
